import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, Wallet as WalletIcon, Trophy } from "lucide-react";
import api from "@/lib/api";
import { ReferralShare } from "@/components/ReferralShare";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";

const StatusPill = ({ status, t }) => {
  const paid = status === "paid";
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] font-mono-spec rounded-sm ${
        paid
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-gold-50 border border-gold-200 text-gold-800"
      }`}
    >
      {paid ? t.credit.paid : t.credit.pending}
    </span>
  );
};

export default function Wallet() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [resending, setResending] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/participant/me");
      setData(res.data);
      setState("ready");
      try {
        const lb = await api.get("/participant/leaderboard");
        setBoard(lb.data);
      } catch { /* ignore */ }
    } catch {
      navigate("/konto/login");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post("/auth/participant/logout");
    navigate("/konto/login");
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/participant/resend-verification");
      toast.success(t.credit.resent);
    } catch {
      toast.error(t.credit.resendError);
    } finally {
      setResending(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500 font-mono-spec text-sm">
        <Loader2 className="animate-spin mr-2" size={20} /> {t.credit.loading}
      </div>
    );
  }

  const rc = data.referral_credit;
  const valid = rc.valid_referrals;
  const required = rc.required;
  const unlocked = rc.status === "pending" || rc.status === "paid";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased" data-testid="wallet-page">
      <div className="border-b spec-border bg-white px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="wallet-logo" className="flex items-center w-fit">
          <Logo markClass="w-6 h-6 text-blue-600" textClass="text-lg" />
        </Link>
        <button
          data-testid="wallet-logout-btn"
          onClick={logout}
          className="text-xs font-mono-spec text-slate-500 hover:text-slate-950 transition-colors"
        >
          {t.credit.logout} →
        </button>
      </div>

      <div className="max-w-xl mx-auto px-4 py-10 space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" data-testid="wallet-title">
          {t.credit.walletTitle}
        </h1>

        {/* Credit summary */}
        <div className="bg-slate-950 text-white p-6 rounded-sm" data-testid="wallet-credit-summary">
          <div className="flex items-center gap-2 text-[11px] font-mono-spec text-slate-400 uppercase tracking-wider">
            <WalletIcon size={14} /> {t.credit.walletCredit}
          </div>
          <div className="mt-3 flex items-end gap-3">
            <span className="text-4xl font-bold" data-testid="wallet-pending-amount">
              €{data.total_pending.toFixed(2)}
            </span>
            <span className="mb-1.5 text-xs font-mono-spec text-gold-400 uppercase">{t.credit.pending}</span>
          </div>
          {data.total_paid > 0 && (
            <div className="mt-1 text-sm font-mono-spec text-emerald-400" data-testid="wallet-paid-amount">
              €{data.total_paid.toFixed(2)} {t.credit.paid}
            </div>
          )}
          <p className="mt-4 text-[11px] font-mono-spec text-slate-400 leading-relaxed">{t.credit.notWithdrawable}</p>
        </div>

        {/* Credit breakdown */}
        <div className="bg-white border spec-border rounded-sm divide-y spec-border" data-testid="wallet-breakdown">
          <div className="p-4 flex items-center justify-between" data-testid="wallet-welcome-row">
            <div>
              <div className="text-sm font-bold text-slate-950">{t.credit.welcomeCredit}</div>
              <div className="text-xs font-mono-spec text-slate-500 mt-0.5">
                €{data.welcome_credit.amount.toFixed(0)}
              </div>
            </div>
            <StatusPill status={data.welcome_credit.status} t={t} />
          </div>

          <div className="p-4" data-testid="wallet-referral-row">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-950">{t.credit.referralCredit}</div>
                <div className="text-xs font-mono-spec text-slate-500 mt-0.5">
                  €{rc.amount.toFixed(0)}
                </div>
              </div>
              {unlocked ? (
                <StatusPill status={rc.status} t={t} />
              ) : (
                <span className="text-xs font-mono-spec text-slate-500" data-testid="wallet-referral-count">
                  {valid} / {required}
                </span>
              )}
            </div>

            {/* Progress dots */}
            <div className="mt-3 flex items-center gap-2" data-testid="wallet-progress">
              {Array.from({ length: required }).map((_, i) => (
                <span
                  key={i}
                  className={`w-3 h-3 rounded-full ${i < valid ? "bg-gold-600" : "bg-slate-200"}`}
                />
              ))}
              <span className="ml-2 text-xs text-slate-500">
                {valid} {t.credit.referralsProgress}
              </span>
            </div>

            {unlocked && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono-spec text-emerald-700" data-testid="wallet-referral-unlocked">
                <CheckCircle2 size={13} /> {t.credit.referralUnlocked} · +€5 {rc.status === "paid" ? t.credit.paid : t.credit.pending}
              </div>
            )}
          </div>
        </div>

        {/* Email verification */}
        <div
          className={`p-4 rounded-sm border flex items-start gap-3 ${
            data.email_verified
              ? "bg-emerald-50 border-emerald-200"
              : "bg-gold-50 border-gold-200"
          }`}
          data-testid="wallet-email-status"
        >
          {data.email_verified ? (
            <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={18} />
          ) : (
            <AlertCircle className="text-gold-700 shrink-0 mt-0.5" size={18} />
          )}
          <div className="flex-1">
            <div className="text-sm font-bold text-slate-900">
              {data.email_verified ? t.credit.emailVerified : t.credit.emailUnverified}
            </div>
            {!data.email_verified && (
              <button
                data-testid="wallet-resend-btn"
                onClick={resend}
                disabled={resending}
                className="mt-1 text-xs font-mono-spec text-gold-800 underline hover:text-gold-900 disabled:opacity-50"
              >
                {t.credit.resendVerification}
              </button>
            )}
          </div>
        </div>

        {/* Referral link */}
        <div className="bg-white border spec-border rounded-sm p-5" data-testid="wallet-referral-share">
          <h2 className="text-base font-bold text-slate-950">{t.credit.inviteTitle}</h2>
          <p className="text-xs font-mono-spec text-slate-500 mt-1 mb-4">{t.credit.yourLink}</p>
          <ReferralShare link={data.referral_link} />
        </div>

        {/* Referral leaderboard */}
        {board && (
          <div className="bg-white border spec-border rounded-sm p-5" data-testid="wallet-leaderboard">
            <div className="flex items-center gap-2">
              <Trophy className="text-gold-600" size={16} />
              <h2 className="text-base font-bold text-slate-950">{t.credit.leaderboardTitle}</h2>
            </div>
            <p className="text-xs font-mono-spec text-slate-500 mt-1 mb-4">{t.credit.leaderboardSub}</p>

            {board.top.length === 0 ? (
              <p className="text-xs text-slate-500" data-testid="wallet-leaderboard-empty">{t.credit.noReferralsYet}</p>
            ) : (
              <ol className="space-y-1.5">
                {board.top.map((e) => (
                  <li
                    key={e.rank}
                    data-testid={`wallet-leaderboard-row-${e.rank}`}
                    className={`flex items-center justify-between px-3 py-2 rounded-sm ${
                      e.is_me ? "bg-gold-50 border border-gold-200" : "bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-mono-spec font-bold w-5 ${e.rank <= 3 ? "text-gold-700" : "text-slate-400"}`}>
                        #{e.rank}
                      </span>
                      <span className="text-sm text-slate-800">
                        {e.is_me ? t.credit.you : e.name}
                      </span>
                    </div>
                    <span className="text-xs font-mono-spec text-slate-600">
                      {e.count} {t.credit.invitesLabel}
                    </span>
                  </li>
                ))}
              </ol>
            )}

            {board.me && !board.top.some((e) => e.is_me) && (
              <div className="mt-3 pt-3 border-t spec-border flex items-center justify-between text-xs" data-testid="wallet-leaderboard-me">
                <span className="font-mono-spec text-slate-500">
                  {t.credit.yourRank}: <b className="text-slate-900">#{board.me.rank}</b>
                </span>
                <span className="font-mono-spec text-slate-600">
                  {board.me.count} {t.credit.invitesLabel}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Device / campaign */}
        <div className="bg-white border spec-border rounded-sm p-5 grid grid-cols-2 gap-4 font-mono-spec text-xs" data-testid="wallet-device">
          <div>
            <div className="text-slate-400">{t.credit.deviceLabel}</div>
            <div className="text-slate-900 font-bold mt-1">{data.device_model}</div>
          </div>
          <div>
            <div className="text-slate-400">{t.credit.campaignStatus}</div>
            <div className="text-slate-900 font-bold mt-1">{data.status}</div>
          </div>
          <div className="col-span-2">
            <Link
              to={`/nachweis/${data.id}`}
              data-testid="wallet-proof-link"
              className="text-gold-700 hover:text-gold-800 font-semibold"
            >
              {t.credit.proofLink} →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
